import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Boxes, CheckCircle2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

function nextDays(n) {
  const out = [];
  for (let i = 0; i < n; i++) { const d = new Date(); d.setDate(d.getDate() + i); out.push(d); }
  return out;
}
const fmt = (d) => d.toISOString().split("T")[0];

export default function CustomerPackages() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sched, setSched] = useState(null); // {purchaseId, entitlement}
  const [date, setDate] = useState(fmt(new Date()));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/packages/purchased/mine").then((r) => setPurchases(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!sched) return;
    setSlot(null);
    api.get(`/services/${sched.entitlement.service_id}/slots`, { params: { date } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [sched, date]);

  const openSched = (purchaseId, entitlement) => { setSched({ purchaseId, entitlement }); setDate(fmt(new Date())); setNotes(""); setSlot(null); };

  const confirmSched = async () => {
    setSaving(true);
    try {
      await api.post(`/packages/purchased/${sched.purchaseId}/schedule`, {
        service_id: sched.entitlement.service_id, date, start_time: slot, notes,
      });
      toast.success("Service scheduled!");
      setSched(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="customer-packages-page">
      <div className="flex items-center gap-2 text-primary"><Boxes className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Your bundles</span></div>
      <h1 className="font-display text-4xl font-semibold tracking-tighter mt-1">My packages</h1>
      <p className="text-muted-foreground mt-2">Schedule each service in your purchased packages.</p>

      {loading ? (
        <p className="text-muted-foreground mt-12">Loading…</p>
      ) : purchases.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-12 text-center mt-10" data-testid="customer-packages-empty">
          <p className="text-muted-foreground">You haven't purchased any packages yet.</p>
          <Link to="/packages" className="inline-block mt-4"><Button className="rounded-sm">Browse packages</Button></Link>
        </div>
      ) : (
        <div className="space-y-6 mt-10">
          {purchases.map((p) => (
            <div key={p.id} className="bg-white border border-border rounded-sm p-6" data-testid={`purchase-${p.id}`}>
              <div className="flex justify-between items-start">
                <h3 className="font-display text-2xl font-medium">{p.package_name}</h3>
                <span className="text-sm text-muted-foreground">Paid ${p.amount_paid}</span>
              </div>
              <div className="mt-4 space-y-3">
                {p.entitlements.map((e) => (
                  <div key={e.service_id} className="flex items-center justify-between border-t border-border pt-3" data-testid={`entitlement-${e.service_id}`}>
                    <div>
                      <div className="font-medium">{e.service_title}</div>
                      <div className="text-xs text-muted-foreground">by {e.provider_name}{e.scheduled ? ` · ${e.date} at ${e.start_time}` : ""}</div>
                    </div>
                    {e.scheduled ? (
                      <span className="flex items-center gap-1 text-sm text-green-700" data-testid={`scheduled-${e.service_id}`}><CheckCircle2 className="w-4 h-4" />Scheduled</span>
                    ) : (
                      <Button size="sm" className="rounded-sm" onClick={() => openSched(p.id, e)} data-testid={`schedule-btn-${e.service_id}`}><CalendarClock className="w-4 h-4 mr-1" />Schedule</Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!sched} onOpenChange={(v) => !v && setSched(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Schedule {sched?.entitlement.service_title}</DialogTitle></DialogHeader>
          <div>
            <p className="text-sm font-medium">Choose a date</p>
            <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
              {nextDays(14).map((d) => {
                const val = fmt(d);
                return (
                  <button key={val} onClick={() => setDate(val)} data-testid={`sched-date-${val}`}
                    className={`shrink-0 w-14 py-2 border rounded-sm text-center transition-all ${date === val ? "bg-secondary text-white border-secondary" : "border-border hover:border-secondary"}`}>
                    <div className="text-[10px] uppercase">{d.toLocaleDateString("en", { weekday: "short" })}</div>
                    <div className="font-display text-base font-semibold">{d.getDate()}</div>
                  </button>
                );
              })}
            </div>
            <p className="text-sm font-medium mt-4">Available slots</p>
            {slots.length === 0 ? (
              <p className="text-muted-foreground text-sm mt-2" data-testid="sched-no-slots">No availability on this day.</p>
            ) : (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {slots.map((s) => (
                  <button key={s.time} disabled={!s.available} onClick={() => setSlot(s.time)} data-testid={`sched-slot-${s.time}`}
                    className={`py-2 border rounded-sm text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed ${slot === s.time ? "bg-primary text-white border-primary" : "border-border hover:border-secondary bg-muted"}`}>
                    {s.time}
                  </button>
                ))}
              </div>
            )}
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="rounded-sm mt-4" data-testid="sched-notes" />
            <Button onClick={confirmSched} disabled={!slot || saving} className="w-full mt-4 rounded-sm" data-testid="confirm-schedule-btn">
              {saving ? "Scheduling…" : slot ? "Confirm schedule" : "Select a slot"}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">Already paid — no additional charge.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
